import PropTypes from 'prop-types';
import React, { PureComponent, Component } from 'react';

import connectToStores from 'fluxible-addons-react/connectToStores';
import DesignSketchesStore from '../../stores/DesignSketchesStore';

export class SketchLayer extends Component {
    render() {
        const svgViewbox =
            -this.props.artboardOrigin.x / this.props.scale +
            ' ' +
            -this.props.artboardOrigin.y / this.props.scale +
            ' ' +
            this.props.artboardSize.width / this.props.scale +
            ' ' +
            this.props.artboardSize.height / this.props.scale;
        return (
            <svg
                ref={el => (this.el = el)}
                role="img"
                width="100%"
                height="100%"
                viewBox={svgViewbox}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    filter:
                        'drop-shadow(1px 1px 0.5px rgba(0,0,0,0.2)) drop-shadow(2px 2px 3px rgba(0,0,0,0.25)) ',
                    pointerEvents: 'none', // Let the user right click and save the image underneath
                }}
            >
                {this.props.children}
            </svg>
        );
    }
}
SketchLayer.propTypes = {
    artboardOrigin: PropTypes.object.isRequired,
    artboardSize: PropTypes.object.isRequired,
    scale: PropTypes.number.isRequired,
};

export class Path extends PureComponent {
    render() {
        if (this.props.path.length === 0) return null;

        let svgPath;
        if (this.props.smooth) {
            svgPath = catmullRomFitting(this.props.path);
        } else {
            svgPath =
                `M ${this.props.path[0].x} ${this.props.path[0].y}` +
                this.props.path
                    .slice(1)
                    .map(point => ` L ${point.x} ${point.y}`)
                    .join(' ');
        }

        return (
            <path
                key="line"
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
                strokeWidth="5px"
                d={svgPath}
                stroke="#ff7e65"
                fill="transparent"
            />
        );
    }
}

Path.PropTypes = {
    path: PropTypes.shape({
        points: PropTypes.arrayOf({
            x: PropTypes.number.isRequired,
            y: PropTypes.number.isRequired,
        }).isRequired,
    }).isRequired,
};

// Connect to Sketch store and provide path data to be rendered by a renderPath prop
export const ProvidePaths = connectToStores(
    ({ paths, renderPath }) => paths.map(renderPath),
    [DesignSketchesStore],
    (ctx, props) => ({
        paths: ctx.getStore(DesignSketchesStore).getPathsFromDesignSketch(props.designId),
    })
);
ProvidePaths.propTypes = {
    designId: PropTypes.number.isRequired,
    renderPath: PropTypes.func.isRequired,
};

/**
 * From https://gist.github.com/nicholaswmin/c2661eb11cad5671d816
 *
 * Interpolates a Catmull-Rom Spline through a series of x/y points
 * Converts the CR Spline to Cubic Beziers for use with SVG items
 *
 * @param  {Array} points - Array of points, each point in object literal holding x/y values
 * @return {String} d - SVG string with cubic bezier curves representing the Catmull-Rom Spline
 */
var catmullRomFitting = function(points) {
    // If 'alpha' is 0.5 then the 'Centripetal' variant is used
    // If 'alpha' is 1 then the 'Chordal' variant is used
    const alpha = 0.5;

    let p0, p1, p2, p3, bp1, bp2, d1, d2, d3, A, B, N, M;
    let d3powA, d2powA, d3pow2A, d2pow2A, d1pow2A, d1powA;
    let d = 'M ' + Math.round(points[0].x) + ',' + Math.round(points[0].y) + ' ';
    let length = points.length;
    for (var i = 0; i < length - 1; i++) {
        p0 = i == 0 ? points[0] : points[i - 1];
        p1 = points[i];
        p2 = points[i + 1];
        p3 = i + 2 < length ? points[i + 2] : p2;

        d1 = Math.sqrt(Math.pow(p0.x - p1.x, 2) + Math.pow(p0.y - p1.y, 2));
        d2 = Math.sqrt(Math.pow(p1.x - p2.x, 2) + Math.pow(p1.y - p2.y, 2));
        d3 = Math.sqrt(Math.pow(p2.x - p3.x, 2) + Math.pow(p2.y - p3.y, 2));

        // Catmull-Rom to Cubic Bezier conversion matrix

        // A = 2d1^2a + 3d1^a * d2^a + d3^2a
        // B = 2d3^2a + 3d3^a * d2^a + d2^2a

        // [   0             1            0          0          ]
        // [   -d2^2a /N     A/N          d1^2a /N   0          ]
        // [   0             d3^2a /M     B/M        -d2^2a /M  ]
        // [   0             0            1          0          ]

        d3powA = Math.pow(d3, alpha);
        d3pow2A = Math.pow(d3, 2 * alpha);
        d2powA = Math.pow(d2, alpha);
        d2pow2A = Math.pow(d2, 2 * alpha);
        d1powA = Math.pow(d1, alpha);
        d1pow2A = Math.pow(d1, 2 * alpha);

        A = 2 * d1pow2A + 3 * d1powA * d2powA + d2pow2A;
        B = 2 * d3pow2A + 3 * d3powA * d2powA + d2pow2A;
        N = 3 * d1powA * (d1powA + d2powA);
        if (N > 0) {
            N = 1 / N;
        }
        M = 3 * d3powA * (d3powA + d2powA);
        if (M > 0) {
            M = 1 / M;
        }

        bp1 = {
            x: (-d2pow2A * p0.x + A * p1.x + d1pow2A * p2.x) * N,
            y: (-d2pow2A * p0.y + A * p1.y + d1pow2A * p2.y) * N,
        };

        bp2 = {
            x: (d3pow2A * p1.x + B * p2.x - d2pow2A * p3.x) * M,
            y: (d3pow2A * p1.y + B * p2.y - d2pow2A * p3.y) * M,
        };

        if (bp1.x == 0 && bp1.y == 0) {
            bp1 = p1;
        }
        if (bp2.x == 0 && bp2.y == 0) {
            bp2 = p2;
        }

        d += 'C' + bp1.x + ',' + bp1.y + ' ' + bp2.x + ',' + bp2.y + ' ' + p2.x + ',' + p2.y + ' ';
    }

    return d;
};



// WEBPACK FOOTER //
// ./~/@99designs/ddo/components/DesignPane/SketchLayer.jsx